/**
 * List, click and search events (docs/contracts.md, Analytics). Loaded with the cards (snippets/band,
 * snippets/deck) and on the search page.
 *
 * - view_item_list: once per list (a card's data-list) when its first card enters the viewport, with
 *   every card of that list. A section swap (filters, the theme editor) reports its lists afresh.
 * - select_item: a card link click or a quick-add size tap, with the list name and the card's index.
 *   Cards outside a list (the cart upsell) carry no data-list and are left to their own module.
 * - apply_filter: per filter value a swap added (filters.js fires collection:filtered).
 * - search: the search page's term and result count; zero_results marks an empty search.
 */
import { googleItem, track as send } from '@wispaire/analytics';

/** Fired on document by filters.js after a filter swap, with { from, to } URLs. */
export const FILTERED = 'collection:filtered';

const CARD = '[data-list]';

/** The GA4 item for a card, or for one of its quick-add sizes when `variant` is given. */
function cardItem(card, variant) {
  const data = card.dataset;
  return googleItem(
    { id: data.productId, title: data.itemName },
    variant ?? { id: data.variantId, title: data.itemVariant, price: Number(data.price) },
    data.list,
    Number(data.index),
  );
}

/** [name, value] for each filter value in `to` that `from` did not have; name is the param's last part. */
function addedFilters(from, to) {
  const before = new URL(from).searchParams;
  return [...new URL(to).searchParams]
    .filter(([key, value]) => key.startsWith('filter.') && !before.getAll(key).includes(value))
    .map(([key, value]) => [key.split('.').at(-1), value]);
}

export function connectLists({ doc = globalThis.document, track = send, Observer = globalThis.IntersectionObserver } = {}) {
  const seen = new Set();
  const observer = new Observer((entries) => {
    for (const { target, isIntersecting } of entries) {
      if (!isIntersecting) continue;
      observer.unobserve(target);
      const list = target.dataset.list;
      if (seen.has(list)) continue;
      seen.add(list);
      const cards = [...doc.querySelectorAll(CARD)].filter((card) => card.dataset.list === list);
      track('view_item_list', { item_list_name: list, items: cards.map((card) => cardItem(card)) });
    }
  });

  const scan = (root) => {
    for (const card of root.querySelectorAll(CARD)) {
      seen.delete(card.dataset.list);
      observer.observe(card);
    }
  };
  scan(doc);

  doc.addEventListener('shopify:section:load', ({ detail }) => {
    const section = doc.getElementById(`shopify-section-${detail?.sectionId}`);
    if (section) scan(section);
  });

  doc.addEventListener('click', ({ target, defaultPrevented }) => {
    const hit = target?.closest?.('.card-quick-add__size, .card__link');
    const card = hit?.closest(CARD);
    if (!card) return;
    // The deck cancels a card link click that only slides a neighbour to the
    // centre or ends a drag: no navigation, no selection. card.js cancels the
    // quick-add size click on purpose, so that one still counts.
    if (defaultPrevented && !hit.value) return;
    // A size button holds its variant id as its value; the card link has none.
    const size = hit.value ? { id: hit.value, title: hit.dataset.variantTitle, price: Number(hit.dataset.price) } : undefined;
    track('select_item', { item_list_name: card.dataset.list, items: [cardItem(card, size)] });
  });

  doc.addEventListener(FILTERED, ({ detail }) => {
    for (const [name, value] of addedFilters(detail.from, detail.to)) {
      track('apply_filter', { filter_name: name, filter_value: value });
    }
  });

  const search = doc.querySelector('[data-search-terms]');
  if (search) {
    const count = Number(search.dataset.searchResults);
    track('search', { search_term: search.dataset.searchTerms, results_count: count, zero_results: count === 0 });
  }
}

if (globalThis.document?.querySelectorAll && globalThis.IntersectionObserver) connectLists();
